import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencySwitcher from './CurrencySwitcher';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import siteLogo from '../assets/logo.png';

const Header = ({ selectedCurrency, onCurrencyChange, isBannerVisible }) => {
  const { t } = useTranslation();
  const { openChat, unreadCount } = useChat();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleMobileDropdown = (e) => {
    e.preventDefault();
    setIsMobileDropdownOpen(prev => !prev);
  };

  const renderUserMenu = (isMobile = false) => {
    
	if (!user) {
      return (
        <div className={`user-menu-container ${isMobile && isMobileDropdownOpen ? 'mobile-dropdown-open' : ''}`}>
          <div className="user-menu-toggle" onClick={isMobile ? toggleMobileDropdown : undefined}>
            <i className="fas fa-user-circle"></i><span>{t('login_register')}</span><i className="fas fa-chevron-down"></i>
          </div>
          <ul className="user-dropdown-menu">
            <li><Link to="/login">{t('login_button')}</Link></li>
            <li><Link to="/register">{t('register_button')}</Link></li>
          </ul>
        </div>
      );
    }
    if (['support', 'admin'].includes(user.role)) {
      const isAdmin = user.role === 'admin';
      return (
        <div className={`user-menu-container ${isMobile && isMobileDropdownOpen ? 'mobile-dropdown-open' : ''}`}>
          <div className="user-menu-toggle" onClick={isMobile ? toggleMobileDropdown : undefined}>
            <i className="fas fa-user-shield"></i><span>{isAdmin ? t('admin_panel') : t('support_panel')}</span><i className="fas fa-chevron-down"></i>
          </div>
          <ul className="user-dropdown-menu">
            {isAdmin && (
              <>
                <li className="menu-title">{t('admin_tools')}</li>
                <li><Link to="/management/user-management">{t('user_management')}</Link></li>
				 <li><Link to="/management/discounts">{t('discount_management')}</Link></li>
				 <li><Link to="/management/banners">{t('banner_management')}</Link></li>
                <li><Link to="/management/statistics">{t('statistics')}</Link></li>
                <li><Link to="/management/pending-payouts">{t('pending_payouts')}</Link></li>
              </>
            )}
            <li className="menu-title">{t('support_panel')}</li>
            <li><Link to="/management/all-jobs">{t('all_jobs_title')}</Link></li>
            <li><Link to="/management/pending-translators">{t('pending_translators')}</Link></li>
			<li><Link to="/management/create-quote">{t('create_new_quote')}</Link></li>
            <li className="logout-item" onClick={handleLogout}><i className="fas fa-sign-out-alt"></i> {t('logout')}</li>
          </ul>
        </div>
      );
    }
    if (user.role === 'translator') {
      return (
        <div className={`user-menu-container ${isMobile && isMobileDropdownOpen ? 'mobile-dropdown-open' : ''}`}>
          <div className="user-menu-toggle" onClick={isMobile ? toggleMobileDropdown : undefined}>
            <i className="fas fa-user-circle"></i><span>{t('account_info')}</span><i className="fas fa-chevron-down"></i>
          </div>
          <ul className="user-dropdown-menu">
            <li className="menu-title">{t('panel_title')}</li>
            <li><Link to="/dashboard/jobs">{t('translation_jobs')}</Link></li>
            <li><Link to="/dashboard/available-jobs">{t('dashboard_available_jobs')}</Link></li>
            <li><Link to="/dashboard/profile/">{t('account_info')}</Link></li>
            <li className="logout-item" onClick={handleLogout}><i className="fas fa-sign-out-alt"></i> {t('logout')}</li>
          </ul>
        </div>
      );
    }
    return (
      <div className={`user-menu-container ${isMobile && isMobileDropdownOpen ? 'mobile-dropdown-open' : ''}`}>
        <div className="user-menu-toggle" onClick={isMobile ? toggleMobileDropdown : undefined}>
          <i className="fas fa-user-circle"></i><span>{t('account_info')}</span><i className="fas fa-chevron-down"></i>
        </div>
        <ul className="user-dropdown-menu">
          <li><Link to="/account/orders">{t('my_orders')}</Link></li>
          <li><Link to="/account/profile">{t('account_info')}</Link></li>
          <li className="logout-item" onClick={handleLogout}><i className="fas fa-sign-out-alt"></i> {t('logout')}</li>
        </ul>
      </div>
    );
  };

   return (
    <header className={`main-header ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}
	style={{ top: isBannerVisible ? '50px' : '0' }}>
      <div className="header-container">
        {/* Ana Sayfa Logosu */}
        <Link to="/" className="logo-container">
          <img src={siteLogo} alt="Quadlingua Logo" className="logo-image" />
        </Link>
        
        <nav className="main-nav desktop-only">
          <Link to="/services">{t('services')}</Link>
        
          <Link to="/contact">{t('contact')}</Link>
        </nav>
        
        <div className="header-actions desktop-only">
          {renderUserMenu(false)}
          <LanguageSwitcher />
          <CurrencySwitcher selectedCurrency={selectedCurrency} onCurrencyChange={onCurrencyChange} />
          {user && ['support', 'admin', 'translator'].includes(user.role) && (
            <button className="header-chat-icon" onClick={() => openChat({ recipientName: t('messages') })}>
              <i className="fas fa-comments"></i>
              {unreadCount > 0 && (<span className="notification-badge">{unreadCount}</span>)}
            </button>
          )}
        </div>

        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          <i className={isMobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
        </button>
        
       <div className="mobile-menu">
          <div className="mobile-menu-header">
            <Link to="/" className="mobile-logo-container" onClick={() => setIsMobileMenuOpen(false)}>
                <img src={siteLogo} alt="Quadlingua Logo" className="logo-image" />
            </Link>
            
            {/* DÜZELTME: Sınıf adı ve ikon değiştirildi */}
            <button className="mobile-menu-back-button" onClick={() => setIsMobileMenuOpen(false)}>
              <i className="fas fa-arrow-left"></i>
            </button>
          </div>
          
          <nav className="mobile-nav-links">
            <Link to="/services" onClick={() => setIsMobileMenuOpen(false)}>{t('services')}</Link>
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>{t('contact')}</Link>
          </nav>

          <div className="mobile-social-icons">
            <a href={t('social_linkedin_url')} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="fa-brands fa-linkedin-in"></i></a>
            <a href={t('social_twitter_url')} target="_blank" rel="noopener noreferrer" aria-label="Twitter"><i className="fa-brands fa-twitter"></i></a>
            <a href={t('social_bluesky_url')} target="_blank" rel="noopener noreferrer" aria-label="Bluesky"><i className="fa-brands fa-bluesky"></i></a>
            <a href={t('social_facebook_url')} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="fa-brands fa-facebook-f"></i></a>
            <a href={t('social_instagram_url')} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="fa-brands fa-instagram"></i></a>
          </div>
          <div className="mobile-actions">
            {renderUserMenu(true)}
            <LanguageSwitcher />
            <CurrencySwitcher selectedCurrency={selectedCurrency} onCurrencyChange={onCurrencyChange} />
            {user && ['support', 'admin', 'translator'].includes(user.role) && (
              <button className="header-chat-icon mobile" onClick={() => openChat({ recipientName: t('messages') })}>
                <i className="fas fa-comments"></i>
                {unreadCount > 0 && (<span className="notification-badge">{unreadCount}</span>)}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;